import * as React from "react"
import AssignmentLesson from "./AssignmentLesson"
import Assessment from "./AssignmentAssessment"
import { toast, ToastContainer } from "react-toastify"
import style from "./styles.module.scss"
import * as modules from "../../models/modules"
import { AssessmentSubmissionResponse, LessonVariety, Type } from "shared/types/moduleTypes"
import { LessonFeedback, TopicQuestionFeedback } from "shared/types/submissionTypes"
import "react-toastify/dist/ReactToastify.css"
import Button from "react-bootstrap/Button"
import Modal from "react-bootstrap/Modal"
import useBoolean from "helpers/useBoolean"
import { BeatLoader } from "react-spinners"
import assignments, { AssessmentQuestionContext, getAssessmentQuestionContexts } from "shared/routes/assignments"
import pureInquiry from "shared/routes/simulations/pureInquiry"
import { AssignmentContext } from "./AssignmentContext"
import { AuthContext } from "AuthContext"
import CustomModal from "components/Modals/CustomModal"
import { parseFormData, formHasSufficientData } from "helpers/formDataHelper"
import AssignmentTopic from "components/Assignment/AssignmentTopic"
import { loadInitialAssessmentData, loadInitialLessonData, loadInitialTopicData } from "./helpers/topicAssignmentHelper"
import { TopicStudentAnswers } from "shared/types/curriculumTypes"
import { DrawingAnswer } from "shared/types/studentTypes"
import { highlightIncompleteLessonQuestions } from "components/Lessons/LessonModule/helpers/lessonHelper"
import moduleRoutes from "shared/routes/moduleRoutes"
import { AssessmentViewOptionsType } from "shared/types/assessmentTypes"
import StudentAssessmentResponses from "pages/Dashboard/Student/Grades/Submission/StudentResponses/StudentAssessmentResponses"
import { LessonFeedbackContext } from "./contexts/LessonFeedbackContext"
import { TopicFeedbackContext } from "./contexts/TopicFeedbackContext"
import { feedback } from "shared/routes/submissionsRoute"
import { useModel } from "@stem-sims/nexus"
import { findById } from "shared/routes/assessments/assessments"

interface AssignmentComponentProps {
    topicId?: string
    assessmentId?: string
    videoId?: string
    assessmentNumber?: number
    submissionId?: string
    moduleName: string
    lessonVariety: LessonVariety
    type: Type,
    goBack?: () => void
    requireCompletion?: boolean
    continueAssessment?: boolean
    oneQuestionMode?: boolean
    randomizable?: boolean
    reviewAnswerSetting?: AssessmentViewOptionsType
}

type ModalType = "resume-lesson" |"resume-topic" | "rework" | "continue-assessment"

export default function AssignmentComponent({ videoId = null, assessmentNumber = null, submissionId, moduleName, lessonVariety, type, goBack,
    requireCompletion, continueAssessment, assessmentId, topicId, oneQuestionMode, randomizable, reviewAnswerSetting }: AssignmentComponentProps) {
    const assignmentContext = React.useContext(AssignmentContext)
    const authContext = React.useContext(AuthContext)
    const { activePart, setActivePart, restartAssignment } = assignmentContext
    const [activeVideoId, setActiveVideoId] = React.useState(videoId)

    const [initialLoading, setInitialLoading] = React.useState(true)
    const [initialLessonData, setInitialLessonData] = React.useState<Record<string, string>>(null)
    const [initialAssessmentData, setInitialAssessmentData] = React.useState<Record<string, string>>(null)
    const [initialAssessmentDrawingData, setInitialAssessmentDrawingData] = React.useState<Record<string, DrawingAnswer>>(null)
    const [initialTopicData, setInitialTopicData] = React.useState<TopicStudentAnswers>(null)
    const [initialPureInquiryData, setInitialPureInquiryData] = React.useState<{ questionAnswers: string[], tableAnswers: string[] }>(null)
    const [initialLessonDrawingData, setInitialLessonDrawingData] = React.useState<Record<string, DrawingAnswer>>(null)

    const [modal, setModal] = React.useState<ModalType>(null) 
    const [finishLessonModalVisible, toggleFinishLessonModalVisible] = useBoolean(false)
    const [lessonCompleteConfirmModal, toggleLessonCompleteConfirmModal] = useBoolean(false)

    const [lessonSubmitFn, setLessonSubmitFn] = React.useState<() => Promise<boolean>>(null)

    const [feedbackLoading, setFeedbackLoading] = React.useState(false)
    const [topicFeedback, setTopicFeedback] = React.useState<TopicQuestionFeedback[]>(null)
    const [lessonFeedback, setLessonFeedback] = React.useState<LessonFeedback>(null)

    const { response: assessmentInfo } = useModel({
        model: findById,
        props: { id: assessmentId },
    })

    const isTeacher = authContext.isLoggedIn && !authContext.isStudent

    React.useEffect(() => {
        if (!submissionId) {
            setInitialLoading(false)
            return
        }
        async function load() {
            try {
                const { data: submission } = await assignments.getSubmission({ submissionId })
                if (lessonVariety === "Guided" && submission.studentLessonId) {
                    const [lessonData, lessonDrawingData] = await loadInitialLessonData(moduleName, type, assignmentContext.guidedLesson?.number, submission.studentLessonId)
                    const [assessmentData, assessmentDrawingData] = await loadInitialAssessmentData(assessmentId, submission?.studentId, submissionId)

                    setInitialLessonData(lessonData)
                    setInitialLessonDrawingData(lessonDrawingData)
                    setInitialAssessmentData(assessmentData)
                    setInitialAssessmentDrawingData(assessmentDrawingData)
                } else if (lessonVariety === "Pure Inquiry" && submission.studentPureInquiryId) {
                    const { data: response } = await pureInquiry.get({
                        pureInquiryId: submission.studentPureInquiryId
                    })
                    setInitialPureInquiryData({
                        questionAnswers: response.questionAnswers,
                        tableAnswers: response.tableAnswers
                    })
                } else if (lessonVariety === "Topic" && submission.studentTopicId) {
                    const topicData = await loadInitialTopicData(submission?.studentTopicId)
                    setInitialTopicData(topicData)

                    if (submission.studentLessonId) {
                        const lessonData = await loadInitialLessonData(moduleName, type, assignmentContext.guidedLesson?.number, submission.studentLessonId)
                        setInitialLessonData(lessonData[0])
                    }

                    if (submission.studentAssessmentId) {
                        const [assessmentData] = await loadInitialAssessmentData(assessmentId, submission?.studentId, submission.id)
                        setInitialAssessmentData(assessmentData)
                    }
                } else if (lessonVariety === "Guided" && submission.studentAssessmentId && assessmentId) {
                    const [assessmentData, assessmentDrawingData] = await loadInitialAssessmentData(assessmentId, submission?.studentId, submissionId)
                    setInitialAssessmentData(assessmentData)
                    setInitialAssessmentDrawingData(assessmentDrawingData)
                }
            } catch {
                toast.error("Error loading initial data")
            } finally {
                setInitialLoading(false)
            }
        }

        load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submissionId])

    React.useEffect(() => {
        if (activeVideoId === null && moduleName) {
            void modules.getVideoID(moduleName)
                .then(({videoPath}) => setActiveVideoId(videoPath))
        }
    }, [moduleName, activeVideoId])

    React.useEffect(() => {
        if (!submissionId) return
        setFeedbackLoading(true)

        feedback({ submissionId })
        .then((res) => {
            setTopicFeedback(res.topicFeedback)
            setLessonFeedback(res.lessonFeedback)
        })
        .finally(() => setFeedbackLoading(false)) 
    }, [submissionId])

    const resumingLesson =
        (initialLessonData && Object.keys(initialLessonData).length > 0) ||
        (initialPureInquiryData && (
            initialPureInquiryData.questionAnswers.length > 0 ||
            initialPureInquiryData.tableAnswers.length > 0
        ))

    const resumingTopic = !!initialTopicData

    React.useEffect(() => {
        if (assignmentContext.reworkAssignmentMode) {
            setModal("rework")
        } else if (!!goBack && resumingLesson && !continueAssessment && !assignmentContext.reworkAssignmentMode) {
            setModal("resume-lesson")
        } else if (resumingTopic && !assignmentContext.reworkAssignmentMode) {
            setModal("resume-topic")
        } else if (continueAssessment) {
            setModal("continue-assessment")
        }
    }, [goBack, resumingLesson, resumingTopic, continueAssessment, assignmentContext.reworkAssignmentMode])

    const [answerReviewMode, setAnswerReivewMode] = React.useState(false)
    const [submission, setSubmission] = React.useState<AssessmentSubmissionResponse>()
    const [assessmentQuestionContexts, setAssessmentQuestionContexts] = React.useState<AssessmentQuestionContext[]>([])
    React.useEffect(() => {
        if (answerReviewMode) {
            moduleRoutes.getAssessmentSubmission({assessmentId: assessmentId, studentId: authContext.studentInfo?.uuid, submissionId: submissionId }).then((response) => {
                setSubmission(response)
            })

            getAssessmentQuestionContexts({assessmentId: assessmentId}).then((contexts) => {
                setAssessmentQuestionContexts(contexts)
            })
        }

    }, [answerReviewMode, assessmentId, submissionId, authContext.studentInfo?.uuid])

    if (initialLoading) {
        return <>
            <ToastContainer />
            <BeatLoader />
        </>
    }

    const navigateAssessmentOrComplete = (assessmentNumber: number) => {
        if (assessmentNumber) {
            setActivePart("assessment")
        } else {
            setActivePart("completed")
        }
    }

    const restartLesson = async () => {
        if (submissionId) {
            moduleRoutes.resetSubmissionActiveTime({ submissionId, newTime: 0 })
        }
        setInitialLessonData({})
        setInitialPureInquiryData({ questionAnswers: [], tableAnswers: [] })
        setInitialLessonDrawingData({})
    }

    const resumeLesson = async () => {
        await moduleRoutes.setSubmissionActiveTime({ submissionId })
        setModal(null)
    }

    const restartTopicAssignment = async () => {
        setInitialTopicData(null)
        setInitialLessonData(null)
        setInitialAssessmentData(null)
        await assignmentContext.restartAssignment()
        setModal(null)
        setActivePart("completed") // this is needed to re-render this entire component so that new submission id is used
        setActivePart("topic")
    }

    const resumeTopicAssignment = async () => {
        await moduleRoutes.setSubmissionActiveTime({ submissionId })
        setModal(null)
    }

    return <>
            <CustomModal
                showModal={modal === "rework"}
                header="Resubmit Assignment"
                body="Your submission has been requested to be reworked by your teacher. Your responses from the submission has been restored."
                onConfirm={async () => {
                    await moduleRoutes.setSubmissionActiveTime({ submissionId })
                    setModal(null)
                }}
                onDecline={() => goBack?.()}
                confirmBtn={{text: "Continue", color: "theme"}}
                declineBtn={{text: "Return to Assignments", color: "secondary"}}
            />

        {activePart === "lesson" && <>
            <Modal show={modal === "resume-lesson"} centered size="lg">
                <Modal.Header>
                    <Modal.Title className="w-100 fw-bold">Resume Lesson</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <p>Your answers from previous attempt have been restored. You can either resume or restart the lesson. As you fill out this lesson, your answers will be automatically saved.</p>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={goBack}>Go Back</Button>
                    <Button variant="secondary" onClick={restartLesson}>Restart <span className="d-none d-sm-inline">Lesson</span></Button>
                    <Button variant="theme" onClick={resumeLesson}>Resume <span className="d-none d-sm-inline">Lesson</span></Button>
                </Modal.Footer>
            </Modal>

            {!feedbackLoading &&
                <LessonFeedbackContext.Provider value={{ feedback: lessonFeedback }}>
                    <AssignmentLesson
                        isPartOfTopic={false}
                        submissionId={submissionId}
                        moduleName={moduleName}
                        type={type}
                        videoId={activeVideoId}
                        hasAssessment={!!assessmentNumber}
                        initialLessonData={initialLessonData}
                        initialPureInquiryData={initialPureInquiryData}
                        initialDrawingData={initialLessonDrawingData}
                        lessonVariety={lessonVariety}
                        isReview={assignmentContext.isReview}
                        preSubmit={(submitFn, lessonFormRef, drawingResponses) => {
                            if (isTeacher && !assignmentContext.demoMode) {
                                navigateAssessmentOrComplete(assessmentNumber)
                            } else {
                                if (goBack) {
                                    const parsedFormData = parseFormData(lessonFormRef.current, true)
                                    const formHasData = formHasSufficientData(parsedFormData, drawingResponses)

                                    if (requireCompletion && !formHasData && lessonVariety !== "Review") {
                                        highlightIncompleteLessonQuestions({ ...parsedFormData, ...drawingResponses })
                                        return toast.error("Please fill out the entire lesson before submitting.")
                                    }

                                    if (formHasData) {
                                        submitFn().then(success => {
                                            if (success) {
                                                if (lessonVariety === "Review") {
                                                    navigateAssessmentOrComplete(assessmentNumber)
                                                } else {
                                                    toggleFinishLessonModalVisible()
                                                }
                                            }
                                        })
                                    } else {
                                        setLessonSubmitFn(() => submitFn)
                                        toggleLessonCompleteConfirmModal()
                                    }
                                }
                            }
                        }}
                    />
                </LessonFeedbackContext.Provider>}

            <Modal show={finishLessonModalVisible} centered size="lg" style={{ zIndex: 1061 }}>
                <Modal.Header>
                    <Modal.Title className="w-100">Lesson Submitted</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <p>After clicking "Begin Assessment", you will start the assessment for this lesson. You will not be able to resume the assessment, so make sure you have adequate time to complete it before starting the assessment. We recommend having 15 minutes available.</p>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={goBack}>Go Back to Assignments</Button>
                    <Button variant="theme" onClick={() => navigateAssessmentOrComplete(assessmentNumber)}>Begin Assessment</Button>
                </Modal.Footer>
            </Modal>

            <CustomModal
                showModal={lessonCompleteConfirmModal}
                header="Submit Lesson"
                body={assignmentContext.isReview ? 
                    "Are you sure you want to proceed to the knowledge check?" : 
                    "Are you sure you want to submit your lesson now? Have you completed the Tables and Questions associated with this lesson?"}
                onConfirm={() => {
                    toggleLessonCompleteConfirmModal() //hide lesson complete confirmation modal first
                    lessonSubmitFn().then((success) => {
                        if (success) {
                            if (assignmentContext.isReview) {
                                navigateAssessmentOrComplete(assessmentNumber)
                            } else {
                                toggleFinishLessonModalVisible()
                            }
                        }
                    })
                }}
                onDecline={toggleLessonCompleteConfirmModal}
                confirmBtn={{text: "Yes", color: "theme"}}
                declineBtn={{text: "No", color: "secondary"}}
            />

            <CustomModal
                showModal={modal === "continue-assessment"}
                header="Continue Assessment"
                body="Do you want to complete the assessment from previous assignment submission?"
                onConfirm={async () => {
                    await moduleRoutes.setSubmissionActiveTime({ submissionId })
                    // continue assessment from previous submission
                    setModal(null)
                    setActivePart("assessment")
                }}
                onDecline={async () => {
                    // restart assignment
                    await restartLesson()
                    await restartAssignment()
                    setActivePart("completed") // this is needed to re-render this entire component so that new submission id is used
                    setActivePart("lesson")
                }}
                confirmBtn={{text: "Continue", color: "theme"}}
                declineBtn={{text: "Restart Assignment", color: "secondary"}}
            />
        </>}
        
        {activePart === "assessment" && !answerReviewMode && assessmentNumber &&
            <Assessment
                isPartOfTopic={false}
                initialAssessmentData={initialAssessmentData}
                initialAssessmentDrawingData={initialAssessmentDrawingData}
                setInitialAssessmentDrawingData={setInitialAssessmentDrawingData}
                assessmentId={assessmentId}
                submissionId={submissionId}
                moduleName={assessmentInfo?.module ?? moduleName}
                requireCompletion={requireCompletion}
                assessmentNumber={assessmentInfo?.number ?? assessmentNumber}
                type={assessmentInfo?.type ?? type}
                submitted={() => {
                    if (isTeacher && !assignmentContext.demoMode) {
                        setActivePart("lesson")
                    } else {
                        setActivePart("completed")
                    }
                }}
                oneQuestionMode={oneQuestionMode}
                randomizable={randomizable}
                isReview={assignmentContext.isReview}
            />
        }

        {activePart === "assessment" && answerReviewMode && assessmentNumber && submission &&
            <div className={style.assignmentContents}>
                <div className="offset-lg-1 col-lg-10">
                    <StudentAssessmentResponses
                        assessmentSubmission={submission}
                        contexts={assessmentQuestionContexts}
                        submissionStatus={"submitted"}
                        assessmentViewOption={reviewAnswerSetting}
                        oneQuestionMode={true}
                        viewType="review"
                        goBack={goBack}
                    />
                </div>
            </div>}

        {activePart === "topic" &&
            <>
                <CustomModal
                    showModal={modal === "resume-topic"}
                    header="Continue Topic Assignment"
                    body="Your answers from previous attempt have been restored. Do you want to continue or restart the assignment?"
                    onConfirm={resumeTopicAssignment}
                    onDecline={restartTopicAssignment}
                    confirmBtn={{text: "Continue", color: "theme"}}
                    declineBtn={{text: "Restart", color: "secondary"}}
                />
                {!feedbackLoading &&
                 <TopicFeedbackContext.Provider value={{
                    feedback: topicFeedback || []
                }}>
                    <LessonFeedbackContext.Provider value={{ feedback: lessonFeedback }}>
                        <AssignmentTopic
                            submissionId={submissionId}
                            initialTopicData={initialTopicData}
                            initialLessonData={initialLessonData}
                            initialAssessmentData={initialAssessmentData}
                            requireCompletion={requireCompletion}
                            topicId={topicId}
                            activeVideoId={activeVideoId}
                        />
                    </LessonFeedbackContext.Provider>
                </TopicFeedbackContext.Provider>}
            </>
        }

        {
            activePart === "completed" &&
            <div className={`text-center ${style.assignmentCompleted} d-flex flex-column align-items-center`}>
                <h2>Completed Assignment</h2>

                {lessonVariety !== "Review" && <>
                    <Button className="mt-3" variant="theme" onClick={goBack}>Return to Assignments</Button>
                </>}

                {lessonVariety === "Review" && <>
                    {reviewAnswerSetting === "Grade" && <>
                        <Button className="mt-3" variant="theme" onClick={goBack}>Return to Core Ideas</Button>
                    </>}

                    {reviewAnswerSetting !== "Grade" && <>
                        <Button className="mt-3" variant="theme" onClick={() => {
                            setActivePart("assessment")
                            setAnswerReivewMode(true)
                        }}>Review Answers</Button>
                        <Button className="mt-3" variant="theme" onClick={goBack}>Return to Core Ideas</Button>
                    </>}

                </>}
            </div>
        }
    </>
}
